import { useSaveData } from '@entities/common/savedData/index.ts';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import type { ProgramEditorValues } from '@features/programs/index.ts';
import { useAppDispatch, useAppSelector } from '@shared/hooks';
import { Program } from '@shared/types/programs';
import { queryClient } from '@src/api/query-client.ts';

const programsAdapter = createEntityAdapter({
  selectId: (program: Program) => program.pk,
});

const programsSlice = createSlice({
  name: 'programs2',
  initialState: programsAdapter.getInitialState(),
  reducers: {
    programAdd: programsAdapter.addOne,
    programsAdd: programsAdapter.addMany,
    programsRemove: programsAdapter.removeAll,
    programRemove: programsAdapter.removeOne,
    programsSet: programsAdapter.setAll,
    programSet: programsAdapter.setOne,
    programUpdate: programsAdapter.updateOne,
    programsReceived(state, action) {
      programsAdapter.setAll(state, action.payload.programs);
    },
  },
});

export const programsReducer = programsSlice.reducer;
export const programsActions = programsSlice.actions;

export const programsSelectors = programsAdapter.getSelectors();

export const useProgram = (id: number) => {
  const dispatch = useAppDispatch();
  const program = useAppSelector((state) =>
    programsSelectors.selectById(state.programsReducer, id)
  );

  const updateProgram = (id: number, changes: Partial<Program>) =>
    dispatch(programsActions.programUpdate({ id: id, changes: changes }));

  const setProgram = (program: Program) => dispatch(programsActions.programSet(program));

  const removeProgram = (id: number) => dispatch(programsActions.programRemove(id));

  return {
    program,
    updateProgram,
    setProgram,
    removeProgram,
  };
};

export const usePrograms = () => {
  const dispatch = useAppDispatch();
  const addPrograms = (programs: Program[]) => dispatch(programsActions.programsAdd(programs));

  const addProgram = (program: Program) => dispatch(programsActions.programAdd(program));

  const ids = useAppSelector((state) => programsSelectors.selectIds(state.programsReducer));
  const programs = useAppSelector((state) => programsSelectors.selectAll(state.programsReducer));

  return {
    addPrograms,
    addProgram,
    ids,
    programs,
  };
};
export const useProgramEditorSavedFormState = () => {
  const {
    data: programEditorSavedFormState,
    doRemoveStorageData: doRemoveProgramEditorSavedFormState,
    doSetStorageData: doSetProgramEditorSavedFormState,
  } = useSaveData<ProgramEditorValues>('programEditorFormState');

  return {
    programEditorSavedFormState,
    doRemoveProgramEditorSavedFormState,
    doSetProgramEditorSavedFormState,
  };
};
export const useProgramsRTK = () => {
  const updateProgramCache = useCallback(({ id, data }: { id: number; data: Partial<Program> }) => {
    queryClient.setQueriesData({ queryKey: ['program', id] }, (oldData: Program) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        ...data,
      };
    });
  }, []);

  const makeProgramFirstOpenFalse = useCallback(
    (id: number) => {
      updateProgramCache({ id, data: { first_open: false } });
    },
    [updateProgramCache]
  );

  return {
    updateProgramCache,
    makeProgramFirstOpenFalse,
  };
};
